import React, { useState, useEffect, Fragment, useMemo } from "react";
import { useRouteMatch, useLocation } from "react-router-dom";
import {
  Button,
  Input,
  Col,
  Row,
  Container,
  Label,
  FormGroup
} from "reactstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import "./index.css";
import ApiCaller from "api/apiCaller";
import api from "api";
import Loading from "components/Loading";
import { Popup } from "components/Popup";
import { AlertConfig, Condition} from 'model/alerting';
import * as utils from "../../../../resources/utils"


const defaultAlert:AlertConfig = {
  is_active: true,
  message_config: {},
  conditions: []
};

interface ModalData {
  isOpen: boolean;
  title: string;
  type: string;
  description: string;
  onPressSubmit: Function;
  onPressCancel: Function;
};

const Condition_Options = [
  { label: '>', value: 'above'},
  { label: '<', value: 'below'},
  { label: '=', value: 'equal'},
  { label: '>=', value: 'above or equal'},
  { label: '<=', value: 'below or equal'},
];

const Condition_Combination_Options = [
  { label: 'And', value: 'and'},
  { label: 'Or', value: 'or'}
];

interface ModalData {
  isOpen: boolean;
  title: string;
  type: string;
  description: string;
  onPressSubmit: Function;
  onPressCancel: Function;
}

const AlertEdit = () => {
  const companyId: any = api.getCurrentCompanyId();
  const baseRoute = 'monitoring/alerting'
  const match = useRouteMatch(`/${baseRoute}/:action/:alert_id`);
  const params: any = match?.params;
  const alertId = params?.alert_id;
  const action = params?.action;
  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    ...defaultAlert
  });

  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    title: "",
    type: "confirm",
    description: "",
    onPressCancel: () => {},
    onPressSubmit: () => {},
  });

  const [isCreate, setIsCreate] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [datasources, setDatasources] = useState([]);
  const [alertProviders, setAlertProviders] = useState([]);
  const [disableButtonRun, setDisableButtonRun] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [chatOptions, setChatOptions] = useState([]);
  const [alertActiveStatus, setAlertActiveStatus] = useState(true);

  const { register, handleSubmit, formState: { errors } } = useForm<AlertConfig>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: alertConfig
  });

  const [isExpressionValid, setIsExpressionValid] = useState(false);
  const [isScheduleExpressionChecked, setIsScheduleExpressionChecked] = useState(false);
  const [nextSchedules, setNextSchedules] = useState([]);
  const [scheduleValidationError, setScheduleValidationError] = useState('');

  const [queryValidationMessage, setQueryValidationMessage] = useState('');
  const [isQueryValid, setIsQueryValid] = useState(false);

  const [messageBodyValidationMessage, setMessageBodyValidationMessage] = useState('');
  const [isMessageBodyValid, setIsMessageBodyValid] = useState(false);

  const checkScheduleExpression = async () => {
    const res:any = await ApiCaller.post(
      `helpers/schedule/validate_expression?company_id=${companyId}`,
      {'schedule_expression': alertConfig.schedule_time}
    )
    if (res.status == 'ok') {
      if (res.data.is_valid) {
        setIsExpressionValid(true);
        setNextSchedules(res.data.next_runs);
        setScheduleValidationError('');
      } else {
        setIsExpressionValid(false);
        setScheduleValidationError(res.data.error_message)
        return false
      }
      setIsScheduleExpressionChecked(true);
      return true
    }
  };

  const checkQuery = async () => {
    const res:any = await ApiCaller.post(
      `helpers/bigquery/validate_query?company_id=${companyId}`,
      {
        'query': alertConfig.query,
        'datasource_id': alertConfig.datasource_id
      }
    )
    if (res.status == 'ok') {
      if (res.data.result === 'valid') {
        setIsQueryValid(true);
        setQueryValidationMessage(res.data.message)
      } else {
        setIsQueryValid(false);
        setQueryValidationMessage(res.data.message)
        return false
      }
      return true
    }
  };

  const checkMessageBody = async () => {
    const res:any = await ApiCaller.post(
      `${baseRoute}/validate/message_body?company_id=${companyId}`,
      {
        'query': alertConfig.query,
        'datasource_id': alertConfig.datasource_id,
        'message_body': alertConfig.message_config?.message_body
      }
    )
    if (res.status == 'ok') {
      if (res.data.result === 'valid') {
        setIsMessageBodyValid(true);
        setMessageBodyValidationMessage(res.data.message)
      } else {
        setIsMessageBodyValid(false);
        setMessageBodyValidationMessage(res.data.message)
        return false
      }
      return true
    }
  }

  const getChats = async ()  => {
    const res:any = await ApiCaller.get(
      `monitoring/alerting/telegram/chats?company_id=${companyId}`
    )
    if (res.status == 'ok') {
      setChatOptions(res.data.chats)
    };
  }

  const onSubmit = handleSubmit(data => {
    if (isExpressionValid && isQueryValid && isMessageBodyValid) {
      saveAlertConfig();
    } else {
      alert('Invalid fields! Please check again')
    }
  });

  const getDatasources = async () => {
    const res:any = await ApiCaller.get(
      `datasource?company_id=${companyId}&is_active=1&limit=100&page=0&sort_type=desc`
    );
    if (res.data && res.data.datasources) {
      setDatasources(res.data.datasources);
    }
  };

  const getAlertProviders = async () => {
    const res:any = await ApiCaller.get(
      `${baseRoute}/providers`
    );
    if (res.data && res.data.alert_providers) {
      setAlertProviders(res.data.alert_providers)
    }
  }

  const getAlert = async () => {
    let uri = `${baseRoute}/${alertId}?company_id=${companyId}`;
    const res:any = await ApiCaller.get(uri);
    if (res.data && res.data.alert) {
      setAlertConfig(res.data.alert);
      setIsExpressionValid(true);
      setIsQueryValid(true);
      setIsMessageBodyValid(true);
      setAlertActiveStatus(res.data.alert.is_active);

      if (res.data.alert.status === 'setting_up') {
        setDisableButtonRun(true);
      } else {
        setDisableButtonRun(false);
      }
      setLoading(false);
    }
  };

  const sendAlert = async () => {
    const res:any = await ApiCaller.post(
      `${baseRoute}/${alertId}/send?company_id=${companyId}`
    );
    if (res.status === 'ok') {
      alert('Run command has been issued. The alert will run any minute from now.');
      setDisableButtonRun(true);
    }
  };

  const handleChangeAlertActiveStatus = async () => {
    const currentState = alertConfig.is_active;
    setAlertActiveStatus(!currentState);
    const res:any = ApiCaller.put(
      `${baseRoute}/${alertId}/active_status?company_id=${companyId}`,
      {'active_status': !currentState}
    );
    setModalData({ ...modalData, isOpen: false });
  }

  const goToAlertEdit = () => {
    window.location.href = `/${baseRoute}/edit/${alertId}`;
  };

  const saveAlertConfig = async () => {
    setIsSaving(true);
    const method = alertConfig.id ? 'put' : 'post';
    const uri = alertConfig.id ? `${baseRoute}/${alertConfig.id}` : baseRoute;
    const res:any = await ApiCaller[method](`${uri}?company_id=${companyId}`, alertConfig);
    if (res.status === 'ok') {
      if (isCreate) {
        window.location.href = `/${baseRoute}/view/${res.alert_id}`;
        alert(res.message);
      } else {
        window.location.href = `/${baseRoute}/view/${alertId}`;
      }
    } else {
      alert(res.message);
    }
    setIsSaving(false);
  };

  const setAlertAttr = (attr:any, value:any, customAttrKey:any) => {
    if (!customAttrKey) {
      setAlertConfig({
        ...alertConfig,
        [attr]: value,
      });
    } else {
      setAlertConfig({
        ...alertConfig,
        [customAttrKey]: {
          // @ts-expect-error
          ...alertConfig[customAttrKey],
          [attr]: value,
        },
      });
    }
  };

  const handleConditionChange = (index: number, attr: string, value: any) => {
    let conditions = [...alertConfig.conditions];
    let condition = {...conditions[index]}
    if (attr === 'column_name') {
      condition.column_name = value
    } else if (attr === 'condition') {
      condition.condition = value
    } else if (attr === 'threshold') {
      condition.threshold = value
    } else if (attr === 'combination') {
      condition.combination = value
    }

    conditions[index] = condition
    setAlertConfig({
      ...alertConfig,
      conditions: conditions
    })
  }

  const addConditionBox = () => {
    const shouldDisableInput = action === 'view';
    if (alertConfig.conditions) {
      return alertConfig.conditions.map((item: Condition, i) => (
        <Row key={i} className="combination-option">
            {i > 0 &&
              <div className="combination-option__input">
                <select
                  className="form-control alerting-condition__input"
                  onChange={e => handleConditionChange(i, 'combination', e.target.value)}
                  value={alertConfig.conditions[i].combination || ''}
                  disabled={shouldDisableInput}
                >
                  <option value="" hidden>-- Select one --</option>
                  {Condition_Combination_Options.map((option:any) =>
                    <option key={option.value || option.id} value={option.value || option.id}>
                      {option.label || option.name}
                    </option>
                  )}
                </select>
              </div>
            }
            <Col xs={4}>
              {i === 0 && <Label>Field name</Label>}
              <input
                className="form-control alerting-condition__input"
                onChange={e => handleConditionChange(i, 'column_name', e.target.value)}
                value={item.column_name || ""}
                disabled={shouldDisableInput}
              />
            </Col>
            <Col xs={2}>
            {i === 0 && <Label>Condition</Label>}
              <select
                className="form-control alerting-condition__input"
                onChange={e => handleConditionChange(i, 'condition', e.target.value)}
                value={alertConfig.conditions[i].condition || ''}
                disabled={shouldDisableInput}
              >
                <option value="" hidden>-- Select one --</option>
                {Condition_Options.map((option:any) =>
                  <option key={option.value || option.id} value={option.value || option.id}>
                    {option.label || option.name}
                  </option>
                )}
              </select>
            </Col>
            <Col xs={4}>
            {i === 0 && <Label>Threshold</Label>}
              <input
                className="form-control alerting-condition__input"
                onChange={e => handleConditionChange(i, 'threshold', e.target.value)}
                value={item.threshold || ""}
                disabled={shouldDisableInput}
              />
            </Col>
            <Col xs={2}>
              <Button
                className="alerting__remove-condition-btn"
                type="button"
                value="remove-condition"
                color={'secondary'}
                onClick={e => removeCondition(i)}
                hidden={shouldDisableInput}
              >
                Remove
              </Button>
            </Col>
          </Row>
      ));
    }
  };

  const addCondition = (event:any) => {
    event.preventDefault();
    var defaultConfig = {
      'column_name': '',
      'condition': 'equal',
      'threshold': '',
      'combination': ''
    };
    if (alertConfig.conditions.length > 0) {
      defaultConfig['combination'] = 'and'
    };

    setAlertConfig({
      ...alertConfig,
      conditions: [
        ...alertConfig.conditions,
        defaultConfig
      ]
    });
    addConditionBox();
  };

  const removeCondition = (index: number) => {
    let conditions = [...alertConfig.conditions];
    conditions.splice(index, 1);
    setAlertConfig({
      ...alertConfig,
      conditions: conditions
    });
  };

  const renderLayoutItem = (layoutItem:any) => {
    const shouldDisableInput = action === 'view';
    let componentLabel = layoutItem.label || layoutItem.attribute;
    componentLabel = componentLabel.replaceAll('_', ' ');
    // @ts-expect-error
    let componentValue = alertConfig[layoutItem.attribute];
    if (layoutItem.customAttrKey) {
      // @ts-expect-error
      componentValue = alertConfig[layoutItem.customAttrKey][layoutItem.attribute];
    }
    if (layoutItem.inputType === 'textarea' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="alerting-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <TextareaAutosize
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setAlertAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
            minRows={3}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.attribute === 'query' &&
            (isQueryValid ?
              (queryValidationMessage &&
                <span className="alerting__validation_success">
                  <i className="material-icons">check</i>
                  {queryValidationMessage}
                </span>
              )
              :
              (queryValidationMessage &&
                <span className="alerting__validation_error">
                  {queryValidationMessage}
                </span>
              )
            )
          }
          {layoutItem.attribute === 'message_body' &&
            (isMessageBodyValid ?
              (messageBodyValidationMessage &&
                <span className="alerting__validation_success">
                  <i className="material-icons">check</i>
                  {messageBodyValidationMessage}
                </span>
              )
              :
              (messageBodyValidationMessage &&
                <span className="alerting__validation_error">
                  {messageBodyValidationMessage}
                </span>
              )
            )
          }
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.inputType === 'select' && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="alerting-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          {layoutItem.attribute === 'chat_id' &&
              <Button
                className="alerting__check_schedule_button"
                type="button"
                value="get-telegram-chat"
                color={'link'}
                onClick={getChats}
                hidden={shouldDisableInput}
              > Refresh </Button>
          }
          <select
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              componentValue ? '' : layoutItem.validation
            )}
            className="form-control"
            onChange={e => setAlertAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || {}}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          >
            <option value="" hidden>-- Select one --</option>
            {layoutItem.options.map((option:any) =>
              <option key={option.value || option.id} value={option.value || option.id}>
                {option.label || option.name}
              </option>
            )}
          </select>
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.inputType === "checkbox" && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="alerting-detail__layout-item__label">{componentLabel}</Label>{' '}
          {layoutItem.tooltip && utils.default.renderTooltip(layoutItem.tooltip)}
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            type="checkbox"
            onChange={e => setAlertAttr(layoutItem.attribute, e.target.checked, layoutItem.customAttrKey)}
            value={componentValue || false}
            checked={componentValue}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.inputType === "flexible_condition" && layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="alerting-detail__layout-item__label">{componentLabel}</Label>{' '}
          <Button
              className="alerting__add-condition-btn"
              type="button"
              value="add-condition"
              color={'primary'}
              onClick={addCondition}
              hidden={shouldDisableInput}
          >
            + Add condition
          </Button>
          {addConditionBox()}
          {layoutItem.error}
        </FormGroup>
      )
    }
    if (layoutItem.isShow) {
      return (
        <FormGroup className={layoutItem.className}>
          <Label className="alerting-detail__layout-item__label">{componentLabel}</Label>
          {layoutItem.tooltip &&
            utils.default.renderTooltip(layoutItem.tooltip)
          }
          <input
            {...register(
              layoutItem.customAttrKey ? `${layoutItem.customAttrKey}.${layoutItem.attribute}` : layoutItem.attribute,
              layoutItem.validation
            )}
            className="form-control"
            onChange={e => setAlertAttr(layoutItem.attribute, e.target.value, layoutItem.customAttrKey)}
            value={componentValue || ""}
            disabled={shouldDisableInput || layoutItem.isDisabledAfterCreate}
          />
          {layoutItem.attribute === 'schedule_time' && isScheduleExpressionChecked &&
          (isExpressionValid &&
            <span className="alerting__validation_success">
              <i className="material-icons">check</i>
              Valid
              <br/>Next schedules:
              {nextSchedules.map(schedule => (
                <li key={schedule}>{schedule}</li>
              )
              )}
            </span>
          )}
          {layoutItem.attribute === 'schedule_time' && scheduleValidationError ?
            <span className="alerting__validation_error">
              {scheduleValidationError}
            </span>
            : layoutItem.error
          }
        </FormGroup>
      );
    }
  };

  useEffect(() => {
    if (alertId && !isCreate) {
      getAlert();
    }
  }, [alertId, isCreate]);

  useEffect(() => {
    if (action && action === 'create') {
      setIsCreate(true);
      setLoading(false);
    } else {
      setIsCreate(false);
    }
  }, [action]);

  useEffect(() => {
    if (isCreate) {
      setAlertConfig({
        ...alertConfig,
        company_id: companyId,
      });
    }
  }, [isCreate]);

  useEffect(() => {
    getDatasources();
    getAlertProviders();
    getChats();
  }, []);

  const renderLayout = [
    {
      groupId: 1,
      name: 'Alert configuration',
      renderCondition: true,
      layoutItems: [
        { attribute: 'name', className: 'required-field',
          isShow: true,
          validation: { required: true},
          error: errors.name && <p className="alerting__validation_error">Name required</p>
        },
        { attribute: 'description', inputType: 'textarea', isShow: true, },
        { attribute: 'tags', isShow: true},
        { attribute: 'schedule_time',
          tooltip: "<br/>If you are <b>Cron Guru</b>, feel free to use cron expression, otherwise you can check docs for available custom options.",
          isShow: true,
          validation: { required: true, validate: checkScheduleExpression},
          error: errors.schedule_time && <p className="alerting__validation_error">Schedule time required</p>
        },
        // source connection
        { attribute: 'datasource_id', label: 'Source connection', inputType: 'select', className: 'required-field',
          isShow: true,
          options: datasources.filter(
            (datasource: any) => datasource.datasource_type_code_name === 'google_bigquery'
          ),
          validation: { required: true},
          error: errors?.datasource_id && <p className="alerting__validation_error">Source connection required</p>
        },
        { attribute: 'query', className: 'required-field', inputType: 'textarea',
          isShow: true,
          validation: { validate: checkQuery },
        },
        { attribute: 'conditions', inputType: "flexible_condition", isShow: true},
        { attribute: 'alert_provider_id', label: 'Alert provider', inputType: 'select', className: 'required-field',
          isShow: true,
          options: alertProviders,
          validation: { required: true},
          error: errors?.alert_provider_id && <p className="alerting__validation_error">Alert provider required</p>
        },
      ],
    },

    {
      groupId: 2,
      name: 'Message Configuration',
      renderCondition: true,
      layoutItems: [
        { attribute: 'chat_id', label: 'Chat', inputType: 'select', className: 'required-field',
          isShow: true,
          options: chatOptions,
          validation: { required: true},
          error: errors?.alert_provider_id && <p className="alerting__validation_error">Chat required</p>
        },
        { attribute: 'message_header', customAttrKey: 'message_config', isShow: true},
        { attribute: 'message_body', inputType: 'textarea', className: 'required-field', customAttrKey: 'message_config',
          isShow: true,
          tooltip: 'Support parameter by using {{parameter_name}}.\
                    <br\><b>Note</b>: parameter_name has to match column name in query result',
          validation: { validate: checkMessageBody },
        },
        { attribute: 'message_footer', customAttrKey: 'message_config', isShow: true},
      ],
    }
  ];

  // display loading
  if (loading) return <Loading />;

  return (
    <Container>
      <Popup
        type={modalData?.type}
        isOpen={modalData?.isOpen}
        title={modalData?.title}
        description={modalData?.description}
        onPressSubmit={modalData?.onPressSubmit}
        onPressCancel={modalData?.onPressCancel}
      />
      <Row>
        <Col xs={12}>
          <h1>{isCreate ? `Create new alert` : `${alertConfig.name}`}</h1>
        </Col>
        <Col xs={12}>
          <div className="alerting-detail__control-buttons">
            {action === 'view' &&
              <Fragment>
                <Button
                  className="alerting__control-button"
                  onClick={() => {
                    setModalData({
                      isOpen: true,
                      title: `${alertActiveStatus ? "Disable" : "Enable"} ${alertConfig.name}?`,
                      type: "confirm",
                      description: `Are you sure you want to ${
                        alertActiveStatus ? "disable" : "enable"
                      } this alert ?`,
                      onPressSubmit: handleChangeAlertActiveStatus,
                      onPressCancel: () => {
                        setModalData({ ...modalData, ...{ isOpen: false } });
                      },
                    });
                  }}
                  color={alertActiveStatus ? "success" : "secondary"}
                >
                  {alertActiveStatus
                    ? <span className="material-icons">check_circle</span>
                    : <span className="material-icons">unpublished</span>
                  }
                  <span>{alertActiveStatus ? "Enabled" : "Disabled"}</span>
                </Button>
                <Button
                  className="alerting__control-button"
                  color="primary" onClick={sendAlert}
                  disabled={disableButtonRun}
                >
                  <span className="material-icons">send</span>
                  <span>{disableButtonRun ? "Sending" : "Send Now"}</span>
                </Button>
                <Button
                  className="alerting__control-button"
                  color="primary"
                  onClick={goToAlertEdit}
                >
                  <span className="material-icons">edit</span>
                  <span>Edit</span>
                </Button>
              </Fragment>
            }
          </div>
        </Col>
        {action === 'view' &&
          <Col xs={12}>
            {alertConfig.description &&
              <p><b>Description</b>: {alertConfig.description}</p>
            }
            <ul>
              <li><b>Status</b>: {utils.default.renderStatusLabel(alertConfig.status)} {utils.default.renderStatusIcon(alertConfig.status)} </li>
              <li><b>Created by</b>: {alertConfig.created_by}</li>
              <li><b>Created on</b>: {alertConfig.created_on}</li>
              <li><b>Changed by</b>: {alertConfig.changed_by}</li>
              <li><b>Changed on</b>: {alertConfig.changed_on}</li>
            </ul>
            {alertConfig.status === 'setup' &&
              <div className="alerting-detail__display_info">
                <div>Alert is being setup, please wait for a while (Usually it takes around 5 minutes).</div>
                <div>If schedule time is set, it will run automatically when it's ready, you will receive the alert message.</div>
              </div>
            }
          </Col>
        }
        <form className="alerting__form" onSubmit={onSubmit}>
            {(action === 'create' || action === 'edit') &&
              <Button
                className="alerting__submit_button"
                type="submit"
                value="submit"
                color={isSaving ? 'secondary' : 'success'}
                disabled={isSaving}
              >
                {isSaving ? 'Saving, please wait...' : 'Save alert'}
              </Button>
            }
            {renderLayout.map(layoutGroup => (
              <Fragment key={layoutGroup.groupId}>
                {layoutGroup.renderCondition &&
                  <div className="alerting-detail__layout-group">
                    <div className="alerting-detail__layout-group__name">{layoutGroup.name}</div>
                    <div className="alerting-detail__layout-group__components">
                      {layoutGroup.layoutItems.map(layoutItem => (
                        <div key={layoutItem.attribute}>
                          {renderLayoutItem(layoutItem)}
                        </div>
                      ))}
                    </div>
                  </div>
                }
              </Fragment>
            ))}
          </form>
      </Row>
    </Container>
  );
}

export default AlertEdit;